import { ReactNode, useMemo } from "react";

// material
import { CssBaseline } from "@mui/material";
import {
  createTheme,
  StyledEngineProvider,
  Theme,
  ThemeProvider,
} from "@mui/material/styles";
import { SxProps } from "@mui/material";

import shape from "./shape";
import typography from "./typography";
import breakpoints from "./breakpoints";
import componentsOverride from "./overrides";
import { darkPalette, lightPalette } from "./palette";
import { buildCustomShadows, buildShadows, ThemeModeName } from "./shadows";
import { useThemeMode } from "../contexts/theme-mode";

//=====================================================

export function buildTheme(mode: ThemeModeName): Theme {
  const theme = createTheme({
    palette: mode === "dark" ? darkPalette : lightPalette,
    shape,
    typography,
    breakpoints,
    shadows: buildShadows(mode) as Theme["shadows"],
    customShadows: buildCustomShadows(mode),
  });
  theme.components = componentsOverride(theme);
  return theme;
}

/**
 * Compat legado: tema ESTÁTICO do modo claro.
 * Código novo deve usar useTheme()/sx em vez deste helper.
 */
const staticLightTheme = buildTheme("light");
export const withTheme = (fn: (theme: Theme) => SxProps) => fn(staticLightTheme);

interface ThemeConfigProps {
  children: ReactNode;
}

export default function ThemeConfig({ children }: ThemeConfigProps) {
  const { resolvedMode } = useThemeMode();
  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
